import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Anchor, Button, Card, Checkbox, FileInput, Group, PasswordInput, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconAlertCircle, IconUpload } from '@tabler/icons-react';
import api from '../services/api';
import { AuthUser } from '../context/AuthContext';
import { mapApiError } from '../utils/apiError';

const roles: AuthUser['user_type'][] = ['JOB_SEEKER', 'JOB_POSTER'];

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const minPasswordLength = 8;
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirm: '',
    user_type: 'JOB_SEEKER' as AuthUser['user_type'],
    first_name: '',
    last_name: '',
    phone_number: '',
    dni: '',
    career: '',
    company_name: '',
    cuit_cuil: '',
    constancia_document: null as File | null,
    cuit_cuil_document: null as File | null,
    termsAccepted: false,
  });

  const careerOptions = [
    { value: 'MUSICOTERAPIA', label: t('careers.musicotherapy') },
    { value: 'PSICOLOGIA', label: t('careers.psychology') },
    { value: 'PROFESORADO_PSICOLOGIA', label: t('careers.psychologyTeaching') },
    { value: 'TERAPIA_OCUPACIONAL', label: t('careers.occupationalTherapy') },
  ];

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (form.password.length < minPasswordLength) {
      setError(t('register.passwordTooShort'));
      return;
    }

    if (form.password !== form.confirm) {
      setError(t('register.passwordMismatch'));
      return;
    }

    const trimmedPhone = form.phone_number.trim();
    if (!trimmedPhone) {
      setError(t('register.phoneRequired'));
      return;
    }
    if (!/^\d{10,}$/.test(trimmedPhone)) {
      setError(t('register.phoneInvalid'));
      return;
    }

    const trimmedDni = form.dni.trim();
    if (form.user_type === 'JOB_SEEKER') {
      if (!trimmedDni) {
        setError(t('register.dniRequired'));
        return;
      }
      if (!/^\d{6,8}$/.test(trimmedDni)) {
        setError(t('register.dniInvalid'));
        return;
      }
      if (!form.career) {
        setError(t('register.careerRequired'));
        return;
      }
      if (!form.constancia_document) {
        setError(t('register.constanciaRequired'));
        return;
      }
    }

    if (form.user_type === 'JOB_POSTER') {
      if (!form.cuit_cuil_document) {
        setError(t('register.cuitCuilRequired'));
        return;
      }
      if (!form.cuit_cuil.trim()) {
        setError(t('register.cuitCuilNumberRequired'));
        return;
      }
      if (!/^\d{11}$/.test(form.cuit_cuil.trim())) {
        setError(t('register.cuitCuilNumberInvalid'));
        return;
      }
    }

    if (!form.termsAccepted) {
      setError(t('register.termsRequired'));
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', form.email);
      formData.append('password', form.password);
      formData.append('password_confirm', form.confirm);
      formData.append('user_type', form.user_type);
      formData.append('first_name', form.first_name);
      formData.append('last_name', form.last_name);
      formData.append('phone_number', trimmedPhone);
      if (form.user_type === 'JOB_SEEKER' && trimmedDni) {
        formData.append('dni', trimmedDni);
      }
      if (form.user_type === 'JOB_SEEKER' && form.career) {
        formData.append('career', form.career);
      }
      if (form.company_name) formData.append('company_name', form.company_name);
      if (form.user_type === 'JOB_POSTER' && form.cuit_cuil.trim()) {
        formData.append('cuit_cuil', form.cuit_cuil.trim());
      }
      if (form.constancia_document) formData.append('constancia_document', form.constancia_document);
      if (form.cuit_cuil_document) formData.append('cuit_cuil_document', form.cuit_cuil_document);
      formData.append('terms_accepted', String(form.termsAccepted));

      await api.registerWithFile(formData);

      // Show success message and redirect to login after 2 seconds
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(mapApiError(err, t, 'register.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card shadow="sm" padding="xl" radius="lg" withBorder>
      <Stack gap="sm">
        <Title order={2}>{t('register.title')}</Title>
        <Text c="dimmed">{t('register.subtitle')}</Text>
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <TextInput
              label={t('register.email')}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="your.email@example.com"
              required
              radius="md"
              disabled={loading}
            />
            <TextInput
              label={t('register.firstName')}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              required
              radius="md"
              disabled={loading}
            />
            <TextInput
              label={t('register.lastName')}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              required
              radius="md"
              disabled={loading}
            />
            <TextInput
              label={t('register.phoneLabel')}
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              placeholder={t('register.phonePlaceholder')}
              required
              radius="md"
              disabled={loading}
            />
            {form.user_type === 'JOB_SEEKER' && (
              <TextInput
                label={t('register.dniLabel')}
                value={form.dni}
                onChange={(e) => setForm({ ...form, dni: e.target.value })}
                required
                radius="md"
                disabled={loading}
              />
            )}
            <PasswordInput
              label={t('register.password')}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              radius="md"
              disabled={loading}
              description={t('register.passwordHint')}
            />
            <PasswordInput
              label={t('register.confirm')}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              radius="md"
              disabled={loading}
            />
            <Select
              label={t('register.role')}
              data={roles.map((role) => ({
                value: role,
                label: role === 'JOB_SEEKER' ? t('register.roles.jobSeeker') : t('register.roles.jobPoster')
              }))}
              value={form.user_type}
              onChange={(value) => value && setForm({ ...form, user_type: value as AuthUser['user_type'] })}
              radius="md"
              disabled={loading}
            />
            {form.user_type === 'JOB_POSTER' && (
              <TextInput
                label={t('register.companyName')}
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                radius="md"
                disabled={loading}
              />
            )}
            {form.user_type === 'JOB_POSTER' && (
              <TextInput
                label={t('register.cuitCuilNumberLabel')}
                value={form.cuit_cuil}
                onChange={(e) => setForm({ ...form, cuit_cuil: e.target.value })}
                placeholder={t('register.cuitCuilNumberPlaceholder')}
                required
                radius="md"
                disabled={loading}
              />
            )}
            {form.user_type === 'JOB_SEEKER' && (
              <Select
                label={t('register.careerLabel')}
                data={careerOptions}
                value={form.career}
                onChange={(value) => setForm({ ...form, career: value || '' })}
                placeholder={t('register.careerPlaceholder')}
                radius="md"
                disabled={loading}
                required
              />
            )}
            {form.user_type === 'JOB_SEEKER' && (
              <FileInput
                label="Constancia de alumno regular (U otra documentación que acredite que sos estudiante de FPSI UBA)"
                placeholder="Upload PDF or image"
                accept=".pdf,image/*"
                value={form.constancia_document}
                onChange={(file) => setForm({ ...form, constancia_document: file })}
                leftSection={<IconUpload size={16} />}
                required
                radius="md"
                disabled={loading}
              />
            )}
            {form.user_type === 'JOB_POSTER' && (
              <FileInput
                label={t('register.cuitCuilLabel')}
                placeholder={t('register.cuitCuilPlaceholder')}
                accept=".pdf,image/*"
                value={form.cuit_cuil_document}
                onChange={(file) => setForm({ ...form, cuit_cuil_document: file })}
                leftSection={<IconUpload size={16} />}
                required
                radius="md"
                disabled={loading}
              />
            )}
            <Group gap="xs" align="center" justify="space-between" wrap="wrap">
              <Checkbox
                checked={form.termsAccepted}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setForm({ ...form, termsAccepted: checked });
                }}
                label={t('register.privacyAcceptLabel')}
              />
            </Group>
            {success && (
              <Alert icon={<IconAlertCircle size={16} />} title="Success" color="green" radius="md">
                {t('register.success')}
              </Alert>
            )}
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
                {error}
              </Alert>
            )}
            <Button type="submit" radius="md" size="md" loading={loading} disabled={success}>
              {t('register.submit')}
            </Button>
          </Stack>
        </form>
        <Text c="dimmed" fz="sm">
          {t('register.haveAccount')}{' '}
          <Anchor component={Link} to="/login" fw={600} c="plum.6">
            {t('register.login')}
          </Anchor>
        </Text>
      </Stack>
    </Card>
  );
};

export default RegisterPage;
